/**
 * SDK-wide constants — single source of truth for limits, defaults and
 * backend-defined magic values.
 */

// ── Session ──────────────────────────────────────────────────────────────────

/** Backend session TTL in milliseconds (30 minutes). */
export const SESSION_TTL_MS = 30 * 60 * 1000;

/** Warn the user this many ms before session expires. */
export const SESSION_WARN_BEFORE_MS = 2 * 60 * 1000;

// ── Network ──────────────────────────────────────────────────────────────────

/** Default request timeout in milliseconds. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Number of automatic retries on transient network errors. */
export const MAX_RETRIES = 2;

/** Delay between retries in milliseconds. */
export const RETRY_DELAY_MS = 1_000;

// ── File uploads ─────────────────────────────────────────────────────────────

/**
 * Largest document file (photo or PDF) the widget uploads: 10 MB, the same
 * limit as the backend and the OCR service. Larger files are refused, never
 * shrunk.
 */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

/** Maximum video size the backend accepts (50 MB). */
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

/** Maximum snapshot / autoshot size (2 MB). */
export const MAX_SNAPSHOT_BYTES = 2 * 1024 * 1024;

/** Accepted MIME types for document images. */
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

/** JPEG quality used when encoding captured frames (0–1). */
export const CAPTURE_JPEG_QUALITY = 0.92;

// ── Liveness scan ────────────────────────────────────────────────────────────

/** Backend-accepted challenge actions (UPPERCASE). Kept for backward compatibility. */
export const CHALLENGE_ACTIONS = ["BLINK", "TURN_LEFT", "TURN_RIGHT", "TURN_HEAD", "OPEN_MOUTH"] as const;

/**
 * Default action tags sent to the backend. The widget no longer runs a
 * discrete multi-step challenge (turn left, then right, then open mouth as
 * separate gated steps) — it's a single passive scan window where the user
 * is asked to blink and turn their head naturally, both within the same
 * window. Both tags are sent so the admin dashboard's "Challenge Actions"
 * label reflects what the user was actually asked to do; the backend does
 * not yet score these individually (see LivenessSerializer.get_liveness_
 * challenges' docstring) — they're descriptive, not a discrete step gate.
 */
export const DEFAULT_CHALLENGE_ACTIONS = ["BLINK", "TURN_HEAD"] as const;

/** Time limit for the natural-motion scan window, in seconds. */
export const LIVENESS_TIME_LIMIT_SECONDS = 12;

// ── Camera ───────────────────────────────────────────────────────────────────

/** Ideal resolution for the front-facing liveness camera. */
export const CAMERA_IDEAL_WIDTH  = 640;
export const CAMERA_IDEAL_HEIGHT = 480;

/**
 * Ideal resolution for rear-facing document capture camera. Captures are
 * uploaded as is, so this is the resolution the OCR and face comparison
 * work from. `ideal` (not `exact`) constraints degrade gracefully on
 * hardware that can't hit this.
 */
export const DOC_CAMERA_IDEAL_WIDTH  = 2000;
export const DOC_CAMERA_IDEAL_HEIGHT = 1500;

/**
 * Video bitrate used by MediaRecorder for liveness recordings — raised from
 * an earlier 250k baseline for sharper frames for SmartComply's face
 * extractor (see VideoRecorder.ts, the only consumer of this constant).
 */
export const RECORDING_BITS_PER_SECOND = 800_000;

// ── Face detection ───────────────────────────────────────────────────────────

/** Max milliseconds to wait for an initial face detection before proceeding. */
export const FACE_DETECT_TIMEOUT_MS = 10_000;

/** Polling interval for the face-present check (ms). */
export const FACE_DETECT_POLL_MS = 100;

// ── UI / UX ──────────────────────────────────────────────────────────────────

/** How long (ms) the success overlay is shown before the flow advances. */
export const RESULT_OVERLAY_DURATION_MS = 2_800;

/** localStorage key used to persist the browser fingerprint ID. */
export const FINGERPRINT_STORAGE_KEY = "_adhere_fid";

/** z-index for the SDK overlay — high enough to sit above any app content. */
export const OVERLAY_Z_INDEX = 99_999;
